import React, { useContext } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { Route, Routes } from 'react-router-dom';
import WordSetPage from 'pages/WordSet/WordSetPage';
// import LevelBasedPage from 'pages/LevelBasedPage';
import AppHeader from 'components/layouts/AppHeader';
import AppFooter from 'components/layouts/AppFooter';
import { pagePaths } from 'routes/paths';
import CategoryOverview from 'pages/CategoryOverview';
import UIBox from 'components/ui/UIBox';
import SubCategoryOverview from 'pages/SubCategoryOverview';
import WordSetCategoryOverview from 'pages/WordSetCategoryOverview';
import HomePage from 'pages/HomePage/HomePage';
import IrregularVerbBrowsePage from 'pages/IrregularVerbs/IrregularVerbBrowsePage';
import IrregularVerbCategoryPage from 'pages/IrregularVerbs/IrregularVerbCategoryPage';
import VocabularyOverview from 'pages/VocabularyOverview/VocabularyOverview';
import VocabularyExport from 'pages/VocabularyExport';
import VocabularyImport from 'pages/VocabularyImport';
import VocabularyWordsPage from 'pages/VocabularyWordsPage';
import VocabularyVerbs from 'pages/VocabularyVerbs';
import VocabularyTest from 'pages/VocabularyTest/VocabularyTest';
import VocabularyTestRunner from 'pages/VocabularyTest/VocabularyTestRunner';
import VocabularyTestResult from 'pages/VocabularyTest/VocabularyTestResult';
import LearningPage from 'pages/Learning/LearningPage';
import LearningSessionPage from 'pages/Learning/LearningSessionPage';
import InActionPage from 'pages/InAction/InActionPage';
import InActionListPage from 'pages/InAction/InActionListPage';
import CommunityPage from 'pages/Community/CommunityPage';
import ProfilePage from 'pages/ProfilePage';
import AchievementsPage from 'pages/AchievementsPage/AchievementsPage';
import IdeasBoardPage from 'features/feedback/ideas/IdeasBoardPage';
import MySubmissionsPage from 'features/feedback/mine/MySubmissionsPage';
import AdminTriagePage from 'features/feedback/admin/AdminTriagePage';
import NotFoundPage404 from './pages/NotFoundPage404';
import LoginPage from 'pages/LoginPage';
import { AuthContext } from 'AuthProvider'; // Додаємо сторінку 404
import { PageIntroProvider } from 'components/PageIntro/PageIntroContext';
import { useHideFooterBelowFold } from 'hooks/useHideFooterBelowFold';

interface IAppRouterProps {}

const AppRouter: React.FC<IAppRouterProps> = () => {
  const { isAuthed } = useContext(AuthContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { wrapperRef, contentRef, paddingBottom } =
    useHideFooterBelowFold(isDesktop);

  return !isAuthed ? (
    <LoginPage />
  ) : (
    <PageIntroProvider>
      <AppHeader />
      <UIBox
        ref={wrapperRef}
        flex={1}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'stretch'}
        sx={{ paddingBottom: `${paddingBottom}px` }}
      >
        <UIBox ref={contentRef}>
          <Routes>
            <Route path={pagePaths.index} element={<HomePage />} />
            <Route path={pagePaths.wordset} element={<WordSetPage />}></Route>
            {/*<Route path={pagePaths.levelBased} element={<LevelBasedPage />} />*/}
            {/*<Route path={pagePaths.topicRelated} element={<TopicRelatedPage />} />*/}
            <Route
              path="wordset/irregular-verb"
              element={<IrregularVerbBrowsePage />}
            />
            <Route
              path="wordset/irregular-verb/pattern/:patternName"
              element={<IrregularVerbCategoryPage axis={'pattern'} />}
            />
            <Route
              path="wordset/irregular-verb/form/:formType"
              element={<IrregularVerbCategoryPage axis={'form'} />}
            />
            <Route
              path="wordset/irregular-verb/family/:baseVerb"
              element={<IrregularVerbCategoryPage axis={'family'} />}
            />
            <Route
              path="wordset/irregular-verb/level/:level"
              element={<IrregularVerbCategoryPage axis={'level'} />}
            />
            <Route
              path={pagePaths.wordSetCategoryOverview}
              element={<WordSetCategoryOverview />}
            />
            <Route
              path={pagePaths.categoryOverview}
              element={<CategoryOverview />}
            />
            <Route
              path={pagePaths.subCategoryOverview}
              element={<SubCategoryOverview />}
            />
            <Route path={pagePaths.vocabulary} element={<VocabularyOverview />} />
            <Route
              path={pagePaths.vocabularyExport}
              element={<VocabularyExport />}
            />
            <Route
              path={pagePaths.vocabularyImport}
              element={<VocabularyImport />}
            />
            <Route
              path={pagePaths.vocabularyWords}
              element={<VocabularyWordsPage wordType={'WORD'} />}
            />
            <Route
              path={pagePaths.vocabularyWordsPage}
              element={<VocabularyWordsPage wordType={'WORD'} />}
            />
            <Route
              path={pagePaths.vocabularyExpressions}
              element={<VocabularyWordsPage wordType={'EXPRESSION'} />}
            />
            <Route
              path={pagePaths.vocabularyExpressionsPage}
              element={<VocabularyWordsPage wordType={'EXPRESSION'} />}
            />
            <Route
              path={pagePaths.vocabularyVerbs}
              element={<VocabularyVerbs />}
            />
            <Route path="vocabulary/verbs" element={<VocabularyVerbs />} />
            <Route
              path={`/${pagePaths.vocabularyTest}/result/:vocabularyTestResultId`}
              element={<VocabularyTestResult />}
            />
            <Route
              path={pagePaths.vocabularyTestRun}
              element={<VocabularyTestRunner />}
            />
            <Route path={pagePaths.vocabularyTest} element={<VocabularyTest />} />
            <Route path={pagePaths.learning} element={<LearningPage />} />
            <Route path="learning" element={<LearningPage />} />
            <Route
              path={pagePaths.learningSession}
              element={<LearningSessionPage />}
            />
            <Route
              path={`${pagePaths.learningSession}/:learningExerciseStep`}
              element={<LearningSessionPage />}
            />
            <Route path="learning/:learningSessionId" element={<LearningSessionPage />} />
            <Route
              path="learning/:learningSessionId/:learningExerciseStep"
              element={<LearningSessionPage />}
            />
            <Route path={pagePaths.inAction} element={<InActionPage />} />
            <Route path={pagePaths.inActionList} element={<InActionListPage />} />
            <Route path={pagePaths.community} element={<CommunityPage />} />
            <Route
              path={pagePaths.communityInActionSets}
              element={<CommunityPage />}
            />
            <Route path={pagePaths.profile} element={<ProfilePage />} />
            <Route
              path={pagePaths.achievements}
              element={<AchievementsPage />}
            />
            <Route path={pagePaths.feedbackIdeas} element={<IdeasBoardPage />} />
            <Route path={pagePaths.feedbackMine} element={<MySubmissionsPage />} />
            <Route path={pagePaths.adminFeedback} element={<AdminTriagePage />} />

            <Route path="*" element={<NotFoundPage404 />} />
          </Routes>
        </UIBox>
      </UIBox>
      <AppFooter />
    </PageIntroProvider>
  );
};

export default AppRouter;
